import React from "react";
import { ArrowDown, ArrowUp, FolderPlus, FolderUp, SearchX, Upload } from "lucide-react";

import { GhostButton } from "@/shared/components/ui-kit";
import { GRID, KbRow, SHOW, UploadingRow, type NodeEdits } from "./KbRow";
import type { AddKind } from "./KbAddMenu";
import { FIRST_DESCENDING, type Row, type Sort, type SortKey } from "./listing";
import type { KbTree } from "./hooks/useTree";
import type { PendingUpload } from "./hooks/useKbUpload";

import type { KbNode, KbUpload } from "@/shared/types";

function Head({ label, sortKey, sort, onSort, className = "" }: {
  label: string;
  sortKey?: SortKey;
  sort: Sort;
  onSort: (sort: Sort) => void;
  className?: string;
}) {
  if (!sortKey) return <span role="columnheader" className={`items-center ${className}`}>{label}</span>;
  const active = sort.key === sortKey;
  const Arrow = sort.descending ? ArrowDown : ArrowUp;
  return (
    <span role="columnheader" aria-sort={active ? (sort.descending ? "descending" : "ascending") : "none"} className={`flex items-center ${className}`}>
      <button
        type="button"
        onClick={() => onSort(active ? { key: sortKey, descending: !sort.descending } : { key: sortKey, descending: FIRST_DESCENDING[sortKey] })}
        className={`press inline-flex items-center gap-1 whitespace-nowrap hover:text-[var(--text-primary)] ${active ? "text-[var(--text-secondary)] font-medium" : ""}`}
      >
        {label}
        {active && <Arrow className="w-3 h-3" strokeWidth={2} />}
      </button>
    </span>
  );
}

/** An empty folder: the drop area (DESIGN §15) in place of the card. */
function EmptyFolder({ onAdd }: { onAdd: (kind: AddKind) => void }) {
  return (
    <div className="rounded-[14px] border-[1.5px] border-dashed border-[var(--hairline-strong)] bg-[var(--surface)] px-5 py-12 flex flex-col items-center text-center gap-3 fade-in">
      <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-2)] flex items-center justify-center">
        <Upload className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-[14px] font-medium text-[var(--text-primary)]">Drop files or folders here</div>
        <div className="mt-1 text-[12.5px] text-[var(--text-tertiary)]">Or add them from your computer. Up to 10 MB a file.</div>
      </div>
      <div className="flex flex-wrap justify-center gap-2 pt-1">
        <GhostButton onClick={() => onAdd("files")}><Upload className="w-3.5 h-3.5" strokeWidth={1.75} /> Upload files</GhostButton>
        <GhostButton onClick={() => onAdd("folder-upload")}><FolderUp className="w-3.5 h-3.5" strokeWidth={1.75} /> Upload folder</GhostButton>
        <GhostButton onClick={() => onAdd("folder")}><FolderPlus className="w-3.5 h-3.5" strokeWidth={1.75} /> New folder</GhostButton>
      </div>
    </div>
  );
}

function NoMatches({ searching, onClear }: { searching: boolean; onClear: () => void }) {
  return (
    <div role="row"><div role="cell" className="px-5 py-12 flex flex-col items-center text-center gap-3 fade-in">
      <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-2)] flex items-center justify-center">
        <SearchX className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-[14px] font-medium text-[var(--text-primary)]">No matches</div>
        <div className="mt-1 text-[12.5px] text-[var(--text-tertiary)]">
          {searching ? "No name or text in this base matches." : "Nothing in this folder matches these filters."}
        </div>
      </div>
      <GhostButton onClick={onClear}>{searching ? "Clear search" : "Clear filters"}</GhostButton>
    </div></div>
  );
}

/* The open folder (or a search across the base) as a drive lists it: the column heads on
   the tray, the rows in the card. Folders first; a folder's tokens and size are the sums of
   the files under it. */
export function KbTable({ rows, tree, me, sort, onSort, pending, uploadingInto, narrowed, onClear, onOpen, edits, onDrop, onAdd }: {
  rows: Row[];
  tree: KbTree;
  me?: string;
  sort: Sort;
  onSort: (sort: Sort) => void;
  pending: PendingUpload[];
  /** Files are on their way into this folder row. */
  uploadingInto: (folder: KbNode) => boolean;
  /** A search or a filter is on: an empty result is "no matches", not an empty folder. */
  narrowed: { searching: boolean; filtering: boolean };
  onClear: () => void;
  onOpen: (node: KbNode) => void;
  edits: NodeEdits;
  onDrop: (uploads: KbUpload[], folder: KbNode) => void;
  onAdd: (kind: AddKind) => void;
}) {
  const empty = !rows.length && !pending.length;
  if (empty && !narrowed.searching && !narrowed.filtering) {
    return (
      <div className="rounded-[18px] bg-[var(--surface-2)] p-1">
        <EmptyFolder onAdd={onAdd} />
      </div>
    );
  }
  const head = { sort, onSort };
  return (
    <div role="table" aria-label={narrowed.searching ? "Search results" : "Files and folders"} className="rounded-[18px] bg-[var(--surface-2)] p-1">
      <div role="row" className={`${GRID} px-3 pt-1.5 pb-2 text-[12px] text-[var(--text-tertiary)]`}>
        <Head label="Name" sortKey="name" {...head} />
        <Head label="Owner" className={SHOW.owner} {...head} />
        <Head label="Status" className={SHOW.status} {...head} />
        <Head label="Modified" sortKey="modified" className={SHOW.modified} {...head} />
        <Head label="Tokens" sortKey="tokens" className={SHOW.tokens} {...head} />
        <Head label="Size" sortKey="size" className="justify-end" {...head} />
        <span role="columnheader" aria-label="Actions" />
      </div>
      <div role="rowgroup" className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift overflow-hidden divide-y divide-[var(--hairline)]">
        {rows.map((row) => (
          <KbRow
            key={row.node.id}
            row={row}
            tree={tree}
            me={me}
            onOpen={() => onOpen(row.node)}
            uploading={row.node.kind === "folder" && uploadingInto(row.node)}
            edits={edits}
            onDrop={(uploads) => onDrop(uploads, row.node)}
          />
        ))}
        {pending.map((p) => <UploadingRow key={p.id} pending={p} />)}
        {empty && <NoMatches searching={narrowed.searching} onClear={onClear} />}
      </div>
    </div>
  );
}
